import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/ui-kit";
import { Modal } from "./Books";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", contact_person: "", commission_pct: 55, settlement_day: 10, nif: "", address: "", notes: "" };

export default function Distributors() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  async function load() { const { data } = await api.get("/distributors"); setItems(data); }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = { ...form, commission_pct: parseFloat(form.commission_pct) || 0, settlement_day: parseInt(form.settlement_day) || 1 };
      if (editing) await api.put(`/distributors/${editing.id}`, payload);
      else {
        const { data: dist } = await api.post("/distributors", payload);
        // Auto-create distributor warehouse
        await api.post("/warehouses", {
          name: `Depósito ${dist.name}`, type: "distribuidor", location_ref: dist.id, active: true,
        });
      }
      toast.success("Distribuidor guardado");
      setShow(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  async function del(id) {
    if (!window.confirm("¿Eliminar distribuidor?")) return;
    await api.delete(`/distributors/${id}`); load();
  }

  return (
    <div data-testid="distributors-page">
      <PageHeader
        title="Distribuidores"
        subtitle="Al crear uno, se generará automáticamente su almacén de depósito."
        actions={
          <button onClick={() => { setEditing(null); setForm(empty); setShow(true); }}
            data-testid="new-distributor-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2">
            <Plus size={16} strokeWidth={1.5} /> Nuevo distribuidor
          </button>
        }
      />
      <Section>
        {items.length === 0 ? <EmptyState title="Sin distribuidores" testid="distributors-empty" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                <th className="text-left px-5 py-3 font-medium">Nombre</th>
                <th className="text-left px-5 py-3 font-medium">Contacto</th>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-right px-5 py-3 font-medium">Comisión %</th>
                <th className="text-right px-5 py-3 font-medium">Liquidación (día)</th>
                <th className="text-right px-5 py-3 font-medium">Acciones</th>
              </tr></thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.id} className="border-t border-line hover:bg-sidebar/30" data-testid={`distributor-row-${d.id}`}>
                    <td className="px-5 py-3 font-medium">{d.name}</td>
                    <td className="px-5 py-3 text-ink2">{d.contact_person || "—"}</td>
                    <td className="px-5 py-3 text-ink2 font-mono text-xs">{d.email || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono">{d.commission_pct}%</td>
                    <td className="px-5 py-3 text-right font-mono">{d.settlement_day}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => { setEditing(d); setForm({ ...empty, ...d }); setShow(true); }} className="text-xs text-ink2 hover:text-ink mr-3">Editar</button>
                      <button onClick={() => del(d.id)} className="text-xs text-crimson hover:opacity-80">Borrar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {show && (
        <Modal title={editing ? "Editar distribuidor" : "Nuevo distribuidor"} onClose={() => setShow(false)}>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="distributor-form">
            <F label="Nombre" required span={2}><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={IN} data-testid="distributor-name" /></F>
            <F label="Persona de contacto"><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className={IN} /></F>
            <F label="NIF"><input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} className={IN} /></F>
            <F label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={IN} /></F>
            <F label="Teléfono"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={IN} /></F>
            <F label="Comisión distribuidor (%)"><input type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className={IN} data-testid="distributor-commission" /></F>
            <F label="Día del mes liquidación"><input type="number" min="1" max="31" value={form.settlement_day} onChange={(e) => setForm({ ...form, settlement_day: e.target.value })} className={IN} /></F>
            <F label="Dirección" span={2}><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={IN} /></F>
            <F label="Notas" span={2}><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={IN} /></F>
            <div className="col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">Cancelar</button>
              <button type="submit" data-testid="distributor-submit" className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const IN = "w-full bg-white border border-line rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
function F({ label, children, required, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}{required && <span className="text-brand"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
